import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../lib/api';
import {
  clearSharedAlbumClaimContext,
  readSharedAlbumClaimContext,
  trackSharedAlbumConversionEvent,
} from '../lib/sharedAlbumClaimContext';
import { readAlbumInvitationContext } from '../lib/albumInvitationContext';

async function completeClaim(authFlow) {
  const context = readSharedAlbumClaimContext();
  if (!context) return null;
  trackSharedAlbumConversionEvent('shared_album_claim_started', { ...context, auth_flow: authFlow });
  try {
    const response = await api.claimAlbumContribution(context.claim_token, authFlow);
    clearSharedAlbumClaimContext();
    trackSharedAlbumConversionEvent('shared_album_claim_succeeded', { ...context, auth_flow: authFlow });
    return { response: response.data, context };
  } catch (error) {
    trackSharedAlbumConversionEvent('shared_album_claim_failed', { ...context, auth_flow: authFlow });
    throw error;
  }
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, demoLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isClaimContinuation = location.search.includes('claim=shared-album');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      const invitation = readAlbumInvitationContext();
      const claim = await completeClaim('login');
      if (claim) {
        toast.success(claim.response?.status === 'already_claimed' ? 'Your contribution is already linked.' : 'Your contribution is linked to your account.');
        navigate(claim.context?.share_slug ? `/a/${claim.context.share_slug}` : '/memories');
      } else {
        toast.success('Welcome back!');
        navigate(invitation?.share_slug ? `/a/${invitation.share_slug}` : '/memories');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Login failed';
      const statusCode = error.response?.status;
      
      if (isClaimContinuation && statusCode === 410) {
        clearSharedAlbumClaimContext();
        toast.error('That contribution claim expired. Your photos are still pending owner review.');
        navigate('/memories');
      } else if (isClaimContinuation && (statusCode === 404 || statusCode === 409 || statusCode === 403)) {
        clearSharedAlbumClaimContext();
        toast.error('We could not link that contribution. Your photos remain pending owner review.');
        navigate('/memories');
      } else if (error.code === 'ERR_NETWORK') {
        toast.error('Network error: Cannot reach server. Please try again later.');
      } else if (statusCode === 520) {
        toast.error('Server is temporarily unavailable. Please try again in a few minutes.');
      } else if (statusCode === 429) {
        toast.error('Too many login attempts. Please wait a few minutes.');
      } else {
        toast.error(`Login failed: ${errorMessage}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async () => {
    setLoading(true);
    try {
      console.log('Starting demo login...');
      await demoLogin();
      console.log('Demo login successful, navigating to dashboard...');
      toast.success('Welcome to the demo! Exploring with sample data.');
      navigate('/memories');
    } catch (error) {
      console.error('Demo login error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Demo login failed';
      if (error.code === 'ERR_NETWORK') {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error(`Demo login failed: ${errorMessage}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#0A0A0A' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-[#141414] rounded-[24px] shadow-card p-8">
          <div className="text-center mb-8">
            <h1 className="font-heading text-4xl text-white mb-2" data-testid="login-heading">Welcome Back</h1>
            <p className="text-stone-300" data-testid="login-subheading">Sign in to your EverBook account</p>
            {isClaimContinuation ? (
              <p className="text-sm text-[#D4A574] mt-2">Sign in to preserve your shared album contribution.</p>
            ) : null}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-stone-200 mb-2" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#0F0F0F] border-transparent focus:border-[#2A2A2A] focus:ring-0 rounded-xl py-3 px-4 text-white"
                placeholder="your@email.com"
                required
                data-testid="login-email-input"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-stone-200" htmlFor="password">
                  Password
                </label>
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-primary hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#0F0F0F] border-transparent focus:border-[#2A2A2A] focus:ring-0 rounded-xl py-3 px-4 text-white"
                placeholder="••••••••"
                required
                data-testid="login-password-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary"
              data-testid="login-submit-button"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-stone-300">
              Don't have an account?{' '}
              <Link to={isClaimContinuation ? '/signup?claim=shared-album' : '/signup'} className="text-primary font-medium hover:underline" data-testid="signup-link">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
